import { useRouter } from "next/router";
import Image from "next/image";
import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Badge,
  Drawer,
  Divider,
  Stack,
  ListItemText,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faShoppingCart,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { NestedMenu } from "../NestedMenu/nestedMenu";
import {
  getMenuItems,
  getAdminMenuItems,
  getReportBuilder,
  getLogoutMenuItems,
} from "@/utils/menuItems";
import { useApplicationsStore } from "@/stores/applicationsStore";
import { useAppStore } from "@/stores/appStore";
import { baseURL } from "@/services/http";
import { useOrgDashboardStore } from "@/stores/orgDashboardStore";
import { useEmploymentRecordsStore } from "@/pages/employment/stores/employmentStore";
import { useEventManagementStore } from "@/stores/eventManagementStore";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import LogoImage from "assets/images/Prof_Reg_OHIO.png";
import { useReportData } from "@/hooks/useReportData";

const StyledFontAwesomeImage = styled(FontAwesomeIcon)(() => ({
  width: 24,
  height: 24,
  color: "#00529B",
}));

const SmallBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    fontSize: "10px",
    width: "16px",
    height: "16px",
  },
}));

const StyledDrawer = styled(Drawer)(() => ({
  "& .MuiDrawer-paper": { width: "100%" },
}));

export const SearchAppBar = ({ user }) => {
  const router = useRouter();
  const [showMenuOverlay, setShowMenuOverlay] = useState(false);
  const { applicationsMenu, fetchApplicationsMenu } = useApplicationsStore();
  const { unreadCount, shoppingCart } = useAppStore();
  const { organizationProfile } = useOrgDashboardStore();
  const { employmentRecords, init: employmentRecordsInit } =
    useEmploymentRecordsStore();
  const {
    selectedTrainingOrganization: trainingOrg,
    permissionsByTrainingOrg,
  } = useEventManagementStore();
  const hasEmploymentRecords = employmentRecords.length > 0;
  const { isFeatureEnabled } = useFeatureFlags();
  const toggleNavMenu = (open) => (event) => {
    //changes the function state according to the value of open
    setShowMenuOverlay(open);
  };

  const { queuedReports, reportPermissions } = useReportData();

  useEffect(() => {
    if (employmentRecordsInit) {
      fetchApplicationsMenu(!hasEmploymentRecords);
    }
  }, [fetchApplicationsMenu, hasEmploymentRecords, employmentRecordsInit]);

  useEffect(() => {
    const handleRouteChange = () => {
      setShowMenuOverlay(false);
    };

    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events, setShowMenuOverlay]);

  return (
    <>
      <Box
        sx={{
          flexGrow: 1,
          mx: "auto",
          ml: { xs: 0, sm: 0, md: 6 },
          mr: { xs: 0, sm: 0, md: 4 },
        }}
      >
        <AppBar position="static" color="secondary" elevation={0}>
          <Toolbar>
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={{ xs: 1, sm: 2, md: 3, lg: 8, xl: 10 }}
              sx={{ flexShrink: 1 }}
            >
              <Box
                sx={{
                  display: { xs: "block", sm: "block", md: "none" },
                  justifyContent: "center",
                  mr: 1,
                }}
              >
                <IconButton size="medium" onClick={toggleNavMenu(true)}>
                  <StyledFontAwesomeImage icon={faBars} alt="Hamburger Menu" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: { xs: "none", sm: "none", md: "flex" },
                }}
              />
            </Stack>
            <Stack
              direction={"row"}
              flexGrow={1}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <Box sx={{ mr: 3, mt: 1 }}>
                <Image
                  src={LogoImage.src}
                  height={50}
                  width={90}
                  alt="OCCRRA"
                  priority
                />
              </Box>
              <Box sx={{}}>
                <Typography
                  sx={{ fontWeight: "bold", display: "flex", fontSize: 14 }}
                >
                  {`${user && user.first_name} ${user && user.last_name}`}{" "}
                </Typography>
                <Typography
                  sx={{ fontWeight: "bold", display: "flex", fontSize: 14 }}
                >
                  OPIN: {user && user.opin_formatted}
                </Typography>
              </Box>
            </Stack>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              sx={{ flexShrink: 1 }}
            >
              <IconButton
                size="large"
                onClick={() => router.push("/notifications")}
              >
                {unreadCount > 0 ? (
                  <SmallBadge badgeContent={unreadCount} color="error">
                    <StyledFontAwesomeImage icon={faBell} size="lg" />
                  </SmallBadge>
                ) : (
                  <StyledFontAwesomeImage icon={faBell} size="lg" />
                )}
              </IconButton>
              <IconButton
                size="large"
                edge="end"
                onClick={() => {
                  window.open(`${baseURL}cart/view_cart?from=pwa`, "_blank");
                }}
              >
                {shoppingCart && shoppingCart.length > 0 ? (
                  <SmallBadge badgeContent={shoppingCart.length} color="error">
                    <StyledFontAwesomeImage icon={faShoppingCart} size="lg" />
                  </SmallBadge>
                ) : (
                  <StyledFontAwesomeImage icon={faShoppingCart} size="lg" />
                )}
              </IconButton>
            </Stack>
          </Toolbar>
        </AppBar>
      </Box>
      {showMenuOverlay && (
        <StyledDrawer open={true} onClose={toggleNavMenu(false)}>
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            <Box sx={{ alignSelf: "flex-start" }}>
              <IconButton onClick={toggleNavMenu(false)}>
                <StyledFontAwesomeImage
                  icon={faTimes}
                  alt="Close Navigation Overlay"
                />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 1 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Image
                src={LogoImage.src}
                height={60}
                width={124}
                alt="OCCRRA"
                priority
              />
            </Box>
            <Box sx={{ justifyContent: "center", display: "flex" }}>
              <NestedMenu
                toggleNavMenu={toggleNavMenu}
                menuItems={getMenuItems(applicationsMenu)}
              />
            </Box>
            <Box sx={{ ml: 2, mr: 2 }}>
              <Divider sx={{ backgroundColor: "#FFFFFF" }} />
            </Box>
            <Box sx={{ justifyContent: "center", display: "flex" }}>
              <NestedMenu
                toggleNavMenu={toggleNavMenu}
                menuItems={getReportBuilder({
                  user,
                  queuedReports,
                  reportPermissions,
                })}
                initialOpenSubMenus={[2]}
              />
            </Box>

            <Box sx={{ ml: 2, mr: 2 }}>
              <Divider sx={{ backgroundColor: "#FFFFFF" }} />
            </Box>
            <Box sx={{ justifyContent: "center", display: "flex" }}>
              <NestedMenu
                toggleNavMenu={toggleNavMenu}
                menuItems={getAdminMenuItems(
                  user,
                  organizationProfile,
                  trainingOrg,
                  !hasEmploymentRecords,
                  isFeatureEnabled,
                  permissionsByTrainingOrg
                )}
                initialOpenSubMenus={[2]}
              />
            </Box>
            <Box sx={{ ml: 2, mr: 2 }}>
              <Divider sx={{ backgroundColor: "#FFFFFF" }} />
            </Box>
            <Box sx={{ justifyContent: "center", display: "flex" }}>
              <NestedMenu
                toggleNavMenu={toggleNavMenu}
                menuItems={getLogoutMenuItems(user)}
              />
            </Box>
            <Box>
              <Divider />
              <Box sx={{ pl: 2 }}>
                <ListItemText>
                  <Typography variant="caption">
                    Contact:{" "}
                    <a href="mailto:support@occrra.org">support@occrra.org</a>
                  </Typography>
                </ListItemText>
              </Box>
              <Box sx={{ pl: 2, display: "flex" }}>
                <ListItemText>
                  <Typography variant="caption">
                    © 2023{" "}
                    <a
                      href="https://occrra.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      OCCRRA
                    </a>
                    {"  "}
                    (The Ohio Child Care Resource and Referral Association). All
                    rights reserved.
                  </Typography>
                </ListItemText>
              </Box>
            </Box>
          </Box>
        </StyledDrawer>
      )}
    </>
  );
};
